import groupBy from 'lodash-es/groupBy';

import { createAddressResolver, type AnchorMode, type TextRange } from './anchor-matching.helper';
import type {
  Address,
  ClauseChoice,
  ClauseSelection,
  DetectedField,
  DetectionResponse,
  ExistingMatch,
  FieldType,
  FlaggedValue,
  Match,
  NewMatch,
  PlaceholderInsertion,
  VariableCategory,
} from './models/ai-template-builder.model';

export type DetectionData = NonNullable<DetectionResponse['data']>;
export type BlankEntry = DetectedField | FlaggedValue;

// Thresholds live here and nowhere else.
const HIGH_CONFIDENCE = 0.85;
const LOW_CONFIDENCE = 0.6;

/** Compound entries share one address; `entryKey` tells them apart. */
export function addressKey({ paragraphId, occurrenceIndex, anchorText }: Address): string {
  return `${paragraphId}::${occurrenceIndex}::${anchorText}`;
}

export function entryKey(entry: BlankEntry): string {
  return `${addressKey(entry)}::${variableName(suggestionOf(entry))}`;
}

export function isFlaggedValue(entry: BlankEntry): entry is FlaggedValue {
  return 'suggestedMatch' in entry;
}

/** Confidence ≥ 0.85 and an existing match, alternative or not. */
export function isAnsweredForYou(field: DetectedField): boolean {
  return field.confidence >= HIGH_CONFIDENCE && field.match.type === 'existing';
}

/** What the customer settled themselves: `entryKey` of a blank, `addressKey` of a clause choice. */
export type DecidedKeys = ReadonlySet<string>;

export function isEntryAnswered(entry: BlankEntry, decided: DecidedKeys): boolean {
  return decided.has(entryKey(entry)) || (!isFlaggedValue(entry) && isAnsweredForYou(entry));
}

export function isClauseAnswered(selection: ClauseSelection, decided: DecidedKeys): boolean {
  return decided.has(addressKey(selection));
}

export function headlineCounts(
  data: DetectionData,
  decided: DecidedKeys = new Set(),
): { answered: number; needsYou: number } {
  const entries = [...data.fields, ...data.flaggedValues];
  const answered =
    entries.filter((entry) => isEntryAnswered(entry, decided)).length +
    data.clauseSelections.filter((selection) => isClauseAnswered(selection, decided)).length;
  const total = entries.length + data.clauseSelections.length;

  return { answered, needsYou: total - answered };
}

/** Settled by the AI alone. A card inside a clause option stays next to its choice. */
export function isCardAnsweredForYou(card: BlankCard): boolean {
  return card.dependsOn === null && card.entries.every((entry) => !isFlaggedValue(entry) && isAnsweredForYou(entry));
}

export interface CardDependency {
  selectionKey: string;
  option: Address;
}

export interface BlankCard {
  kind: 'blank';
  key: string;
  title: string;
  paragraphId: number;
  entries: [BlankEntry, ...BlankEntry[]];
  dependsOn: CardDependency | null;
  /** Every option holding the card, innermost first: removing any of them hides it. */
  owners: CardDependency[];
}

export type Card =
  | BlankCard
  | {
      kind: 'clause';
      key: string;
      title: string;
      paragraphId: number;
      selection: ClauseSelection;
      /** Every option of another choice holding this one: removing any of them hides it. */
      owners: CardDependency[];
    };

interface Located<T> {
  item: T;
  paragraphId: number;
  start: number;
}

/** A card for every detected field. Screen order (card rules): any_of, one_of, then blanks; a blank inside an option follows its choice. */
export function buildCards(data: DetectionData): Card[] {
  const locate = locator(data);
  const enclosingOwnersOf = enclosingOwnersFinder(data);
  const clauseCards = data.clauseSelections.map((selection) =>
    locate(clauseCard(selection, enclosingOwnersOf(selection)), selection, 'verbatim'),
  );
  const blankCards = blankCardsOf(data).map((card) => locate(card, card.entries[0], 'field'));
  const inDocumentOrder = <T>(located: Located<T>[]) => located.toSorted(byDocumentOrder).map(({ item }) => item);
  const dependentsOf = (selectionKey: string) =>
    inDocumentOrder(blankCards.filter(({ item }) => item.dependsOn?.selectionKey === selectionKey));
  const clausesOfType = (selectionType: ClauseSelection['selectionType']) =>
    inDocumentOrder(clauseCards.filter(({ item }) => item.selection.selectionType === selectionType));

  return [
    ...[...clausesOfType('any_of'), ...clausesOfType('one_of')].flatMap((card) => [card, ...dependentsOf(card.key)]),
    ...inDocumentOrder(blankCards.filter(({ item }) => item.dependsOn === null)),
  ];
}

function clauseCard(selection: ClauseSelection, owners: CardDependency[]): Extract<Card, { kind: 'clause' }> {
  return {
    kind: 'clause',
    key: addressKey(selection),
    title: selection.title,
    paragraphId: selection.paragraphId,
    selection,
    owners,
  };
}

// New proposals sharing a paragraph and a dependency share one card ("Create them all").
function blankCardsOf(data: DetectionData): BlankCard[] {
  const ownersOf = ownersFinder(data);
  const dependencyOf = (entry: BlankEntry) => ownersOf(entry)[0] ?? null;
  const cardOf = ([first, ...rest]: BlankEntry[]): BlankCard | null =>
    first
      ? {
          kind: 'blank',
          key: entryKey(first),
          title: first.title,
          paragraphId: first.paragraphId,
          entries: [first, ...rest],
          dependsOn: dependencyOf(first),
          owners: ownersOf(first),
        }
      : null;
  const proposals = groupBy(
    data.fields.filter((field) => field.match.type === 'new'),
    (field) => `${field.paragraphId}::${dependencyKey(dependencyOf(field))}`,
  );

  return [
    ...data.fields.filter((field) => field.match.type !== 'new').map((field) => cardOf([field])),
    ...Object.values(proposals).map(cardOf),
    ...data.flaggedValues.map((flagged) => cardOf([flagged])),
  ].filter((card) => card !== null);
}

function dependencyKey(dependency: CardDependency | null): string {
  return dependency ? `${dependency.selectionKey}::${addressKey(dependency.option)}` : '';
}

// Innermost first (a weekday blank depends on its checkbox); on a tie, an option comes before a continuation.
function ownersFinder(data: DetectionData): (entry: BlankEntry) => CardDependency[] {
  const findOwners = ownerFinder(data, data.clauseSelections);
  const innermostFirst = (first: OptionSpan, second: OptionSpan) =>
    length(first.range) - length(second.range) || Number(first.isContinuation) - Number(second.isContinuation);

  return (entry) =>
    findOwners(entry, 'field')
      .toSorted(innermostFirst)
      .map(({ dependency }) => dependency);
}

// A selection's own address is its first option's, so its own options are not owners.
function enclosingOwnersFinder(data: DetectionData): (selection: ClauseSelection) => CardDependency[] {
  const findOwners = ownerFinder(data, data.clauseSelections);

  return (selection) =>
    findOwners(selection, 'verbatim')
      .map(({ dependency }) => dependency)
      .filter((owner) => owner.selectionKey !== addressKey(selection));
}

interface OptionSpan {
  dependency: CardDependency;
  paragraphId: number;
  range: TextRange | null;
  isContinuation: boolean;
}

function ownerFinder(
  data: DetectionData,
  selections: ClauseSelection[],
): (address: Address, mode: AnchorMode) => OptionSpan[] {
  const rangeOf = rangeResolver(data);
  const spans = selections.flatMap((selection) =>
    selection.options.flatMap((option) =>
      [option, ...(option.continuation ?? [])].map((span) => ({
        dependency: { selectionKey: addressKey(selection), option },
        paragraphId: span.paragraphId,
        range: rangeOf(span, 'verbatim'),
        isContinuation: span !== option,
      })),
    ),
  );

  return (address, mode) => {
    const range = rangeOf(address, mode);

    return spans.filter(
      (span) => span.paragraphId === address.paragraphId && range !== null && contains(span.range, range),
    );
  };
}

function contains(outer: TextRange | null, inner: TextRange): boolean {
  return outer !== null && outer.start <= inner.start && inner.end <= outer.end;
}

function length(range: TextRange | null): number {
  return range ? range.end - range.start : Number.POSITIVE_INFINITY;
}

function rangeResolver(data: DetectionData): (address: Address, mode: AnchorMode) => TextRange | null {
  const textOf = new Map(data.document.paragraphs.map((paragraph) => [paragraph.id, paragraph.text]));
  const resolveAddress = createAddressResolver();

  return (address, mode) => {
    const text = textOf.get(address.paragraphId);

    return text === undefined ? null : resolveAddress(text, address, mode);
  };
}

// Where an item sits in the document; without the text, the occurrence index is the best guess.
function locator(data: DetectionData): <T>(item: T, address: Address, mode: AnchorMode) => Located<T> {
  const rangeOf = rangeResolver(data);

  return (item, address, mode) => ({
    item,
    paragraphId: address.paragraphId,
    start: rangeOf(address, mode)?.start ?? address.occurrenceIndex,
  });
}

function byDocumentOrder<T>(first: Located<T>, second: Located<T>): number {
  return first.paragraphId - second.paragraphId || first.start - second.start;
}

export type BlankDecision =
  | { kind: 'use_existing'; variable: string }
  | { kind: 'create_new'; name: string; label: string; fieldType: FieldType; category: VariableCategory }
  | { kind: 'leave' };

export type ClauseDecision =
  | { kind: 'keep_options'; kept: Address[] }
  | { kind: 'ask_each_time'; name: string; label: string }
  | { kind: 'leave' };

export function suggestionOf(entry: BlankEntry): Match {
  return isFlaggedValue(entry) ? entry.suggestedMatch : entry.match;
}

function variableName(match: Match): string {
  return match.type === 'existing' ? match.variable : match.name;
}

export function matchDecision(match: ExistingMatch | NewMatch): BlankDecision {
  return match.type === 'existing'
    ? { kind: 'use_existing', variable: match.variable }
    : {
        kind: 'create_new',
        name: match.name,
        label: match.label,
        fieldType: match.fieldType,
        category: match.category,
      };
}

/** A field they have, then a new field; anything below LOW_CONFIDENCE stays as written. */
export function defaultBlankDecision(entry: BlankEntry): BlankDecision {
  return entry.confidence < LOW_CONFIDENCE ? { kind: 'leave' } : matchDecision(suggestionOf(entry));
}

/** A flagged value is a suggestion: until the customer confirms it, it stays as written. */
export function effectiveBlankDecision(
  entry: BlankEntry,
  blankDecisions: ReadonlyMap<string, BlankDecision>,
): BlankDecision {
  const decision = blankDecisions.get(entryKey(entry));

  if (decision) {
    return decision;
  }

  return isFlaggedValue(entry) ? { kind: 'leave' } : defaultBlankDecision(entry);
}

/** Pick-one asks each time unless an option holds a blank, which it would swallow; pick-any keeps all. */
export function defaultClauseDecision(selection: ClauseSelection, data: DetectionData): ClauseDecision {
  if (selection.selectionType === 'any_of') {
    return { kind: 'keep_options', kept: selection.options.map(addressOf) };
  }

  const findOwners = ownerFinder(data, [selection]);
  const holdsBlank = [...data.fields, ...data.flaggedValues].some((entry) => findOwners(entry, 'field').length > 0);

  return selection.proposedVariable && !holdsBlank
    ? { kind: 'ask_each_time', name: selection.proposedVariable.name, label: selection.proposedVariable.label }
    : { kind: 'leave' };
}

/** Keyed by `addressKey` of the selection. A card with no decision yet is visible. */
export function isCardVisible(card: Card, clauseDecisions: ReadonlyMap<string, ClauseDecision>): boolean {
  return areOwnersKept(card.owners, clauseDecisions);
}

function areOwnersKept(owners: CardDependency[], clauseDecisions: ReadonlyMap<string, ClauseDecision>): boolean {
  return owners.every((owner) => isOwnerKept(owner, clauseDecisions));
}

function isOwnerKept(owner: CardDependency, clauseDecisions: ReadonlyMap<string, ClauseDecision>): boolean {
  const { selectionKey, option } = owner;
  const decision = clauseDecisions.get(selectionKey);

  if (!decision || decision.kind === 'leave') {
    return true;
  }

  return (
    decision.kind === 'keep_options' &&
    (decision.kept.length === 0 || decision.kept.some((kept) => addressKey(kept) === addressKey(option)))
  );
}

function addressOf({ paragraphId, anchorText, occurrenceIndex }: Address): Address {
  return { paragraphId, anchorText, occurrenceIndex };
}

/** A missing decision takes its default, a flagged value excepted. An entry inside any removed option or a Select field is left out, card or not. */
export function toInsertions(
  data: DetectionData,
  blankDecisions: ReadonlyMap<string, BlankDecision>,
  clauseDecisions: ReadonlyMap<string, ClauseDecision>,
): PlaceholderInsertion[] {
  const ownersOf = ownersFinder(data);
  const insertions = [...data.fields, ...data.flaggedValues].flatMap((entry): PlaceholderInsertion[] => {
    const decision = effectiveBlankDecision(entry, blankDecisions);

    if (decision.kind === 'leave' || !areOwnersKept(ownersOf(entry), clauseDecisions)) {
      return [];
    }

    const placeholderName = decision.kind === 'use_existing' ? decision.variable : decision.name;

    return isFlaggedValue(entry)
      ? [{ ...addressOf(entry), action: 'replace', placeholderName, joinSeparator: null }]
      : [{ ...addressOf(entry), action: entry.action, placeholderName, joinSeparator: entry.joinSeparator }];
  });

  return withoutTrailingSeparators(insertions);
}

// When the last entry of a compound is left out, the one now last must not carry a separator.
function withoutTrailingSeparators(insertions: PlaceholderInsertion[]): PlaceholderInsertion[] {
  const lastOfSpot = new Map(insertions.map((insertion, index) => [addressKey(insertion), index]));

  return insertions.map((insertion, index) =>
    lastOfSpot.get(addressKey(insertion)) === index ? { ...insertion, joinSeparator: null } : insertion,
  );
}

/** Keeping every option or none is omitted, and so is a choice inside a removed option or a Select field. */
export function toClauseChoices(
  data: DetectionData,
  clauseDecisions: ReadonlyMap<string, ClauseDecision>,
): ClauseChoice[] {
  const enclosingOwnersOf = enclosingOwnersFinder(data);

  return data.clauseSelections.flatMap((selection): ClauseChoice[] => {
    const decision = clauseDecisions.get(addressKey(selection)) ?? defaultClauseDecision(selection, data);

    if (!areOwnersKept(enclosingOwnersOf(selection), clauseDecisions)) {
      return [];
    }

    if (decision.kind === 'ask_each_time') {
      return [{ ...addressOf(selection), resolution: 'replace_with_variable', placeholderName: decision.name }];
    }

    const keepsSome = decision.kind === 'keep_options' && decision.kept.length > 0;
    const keepsAll = decision.kind === 'keep_options' && decision.kept.length === selection.options.length;

    return decision.kind === 'keep_options' && keepsSome && !keepsAll
      ? [{ ...addressOf(selection), resolution: 'keep_options', keptOptions: decision.kept.map(addressOf) }]
      : [];
  });
}
